import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useContext, useCallback } from 'react';
import {
  useCreateComment,
  useCreateThread,
  useDeleteComment,
  useDeleteThread,
  useEditComment,
  useMarkThreadAsResolved,
  useMarkThreadAsUnresolved,
  useBroadcastEvent,
} from '@liveblocks/react/suspense';
import type { RoomType } from './RoomRegistrar';
import type { CommentBody } from '@liveblocks/client';
import { getHostname } from 'utils/getHostname';

interface RoomOperations {
  createThread: (body: CommentBody, blockId: string | null) => Promise<{ id: string }>;
  addComment: (threadId: string, body: CommentBody) => Promise<{ id: string }>;
  editComment: (threadId: string, commentId: string, body: CommentBody) => Promise<void>;
  deleteComment: (threadId: string, commentId: string) => Promise<void>;
  resolveThread: (threadId: string) => Promise<void>;
  unResolveThread: (threadId: string) => Promise<void>;
  deleteThread: (threadId: string) => Promise<void>;
}

const RoomOperationsContext = createContext<RoomOperations | null>(null);

interface RoomOperationsProviderProps {
  children: ReactNode;
  roomType: RoomType;
}

export const BroadcastThreadsEvent = 'comments:threads-updated';

export const RoomOperationsProvider: React.FC<RoomOperationsProviderProps> = ({ children }) => {
  const createThread = useCreateThread();
  const createComment = useCreateComment();
  const editComment = useEditComment();
  const deleteComment = useDeleteComment();
  const markThreadAsResolved = useMarkThreadAsResolved();
  const unMarkThreadAsResolved = useMarkThreadAsUnresolved();
  const deleteThread = useDeleteThread();
  const broadcast = useBroadcastEvent();

  const handleCreateThread = useCallback(
    async (body: CommentBody, blockId: string | null) => {
      const hostname = getHostname();

      const thread = createThread({
        body,
        metadata: {
          blockId: blockId || '',
          hostname: hostname || '',
        },
      });
      broadcast({ type: BroadcastThreadsEvent });
      return thread;
    },
    [createThread, broadcast]
  );

  const handleCreateComment = useCallback(
    async (threadId: string, body: CommentBody) => {
      const comment = createComment({
        threadId,
        body,
      });
      return comment;
    },
    [createComment]
  );

  const handleEditComment = useCallback(
    async (threadId: string, commentId: string, body: CommentBody) => {
      editComment({
        threadId,
        commentId,
        body,
      });
    },
    [editComment]
  );

  const handleDeleteComment = useCallback(
    async (threadId: string, commentId: string) => {
      deleteComment({ threadId, commentId });
    },
    [deleteComment]
  );

  const handleResolveThread = useCallback(
    async (threadId: string) => {
      markThreadAsResolved(threadId);
      broadcast({ type: BroadcastThreadsEvent });
    },
    [markThreadAsResolved, broadcast]
  );

  const handleDeleteThread = useCallback(
    async (threadId: string) => {
      deleteThread(threadId);
      broadcast({ type: BroadcastThreadsEvent });
    },
    [deleteThread, broadcast]
  );

  const handleUnResolveThread = useCallback(
    async (threadId: string) => {
      unMarkThreadAsResolved(threadId);
      broadcast({ type: BroadcastThreadsEvent });
    },
    [unMarkThreadAsResolved, broadcast]
  );

  const operations: RoomOperations = {
    createThread: handleCreateThread,
    addComment: handleCreateComment,
    editComment: handleEditComment,
    deleteComment: handleDeleteComment,
    resolveThread: handleResolveThread,
    unResolveThread: handleUnResolveThread,
    deleteThread: handleDeleteThread,
  };

  return <RoomOperationsContext.Provider value={operations}>{children}</RoomOperationsContext.Provider>;
};

export const useRoomOperations = (): RoomOperations => {
  const context = useContext(RoomOperationsContext);
  if (!context) {
    throw new Error('useRoomOperations must be used within a RoomOperationsProvider');
  }
  return context;
};
